import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { GLabel } from '../../pdf/glabel';
import { calcOrigin } from '../../core/genHelper';
import { IItemCount } from '@educraft/base';
import { anagram } from '../../core/variation';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/transforms';

/**
 * Kolicky_Presmycky (Peg Anagrams) - Multiple-choice anagram cards
 *
 * Layout: 3 landscape pages with increasing option counts (2, 3, 4).
 * Each card: image + rotated scrambled word options below.
 *
 * Migrated from C# EduFrame.Img10.Generators.Kolicky__Presmycky
 */
export class Kolicky__Presmycky implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(5, 2, margin, PdfSize.A4H);
    const allTexts = opts.list.map((t) => t.text);

    for (let count = 2; count <= 4; count++) {
      const totalPages = Math.ceil(opts.list.length / 10);

      for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
        const page = pdf.addPage(true);
        const startItem = pageIdx * 10;
        const endItem = Math.min(startItem + 10, opts.list.length);

        const optionSize = { width: size.width / count, height: PdfUnit.FromMillimeters(30) };
        const labelFont = page.adjustFont(
          optionSize.height - PdfUnit.FromMillimeters(3),
          allTexts,
          GUtil.baseFont
        );

        for (let x = 0; x < 5; x++) {
          for (let y = 0; y < 2; y++) {
            const ind = y * 5 + x;
            const itemInd = startItem + ind;

            const rect = new PdfRect(
              origin.x + x * size.width,
              origin.y + y * size.height,
              size.width,
              size.height
            );
            const r1 = new PdfRect(
              rect.left,
              rect.top,
              rect.width,
              rect.height - optionSize.height
            );

            if (itemInd < endItem) {
              page.drawLabel(opts.list[itemInd].label, { rect: r1 });
              page.drawRectangle(GUtil.basePen, null, rect);

              const label = opts.list[itemInd].text;
              const hash = new Set<string>([label.toUpperCase()]);

              while (hash.size < count) {
                const w = anagram(label, opts.data.random);
                hash.add(w);
              }

              const options = Array.from(hash);
              for (let i = options.length - 1; i > 0; i--) {
                const j = opts.data.random.next(0, i + 1);
                [options[i], options[j]] = [options[j], options[i]];
              }

              for (let i = 0; i < options.length; i++) {
                const r = new PdfRect(
                  rect.left + i * optionSize.width,
                  rect.bottom - optionSize.height,
                  optionSize.width,
                  optionSize.height
                );
                const r2 = new PdfRect(
                  r.centerX - r.height / 2,
                  r.centerY - r.width / 2,
                  r.height,
                  r.width
                );

                page.save();
                page.rotateAtTransform(270, new PdfPoint(r.centerX, r.centerY));
                page.drawLabel(GLabel.fromText(options[i]), { rect: r2, font: labelFont });
                page.restore();

                page.drawRectangle(GUtil.basePen, null, r);
              }
            } else {
              page.drawRectangle(GUtil.basePen, null, rect);
              for (let i = 0; i < count; i++) {
                const r = new PdfRect(
                  rect.left + i * optionSize.width,
                  rect.bottom - optionSize.height,
                  optionSize.width,
                  optionSize.height
                );
                page.drawRectangle(GUtil.basePen, null, r);
              }
            }
          }
        }
      }
    }
  }
}
